import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Calendar, User, Tag, Eye, Heart, MessageSquare,
  Play, ExternalLink, Share2, Clock, Building, Loader2,
  FileText, MessageCircle, Music, Ticket, Briefcase, Image, Video,
  ShoppingBag, Home, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Content type icons
const CONTENT_ICONS = {
  discussion: MessageSquare,
  community_article: Users,
  blog_article: FileText,
  soundcloud_post: Music,
  classified: Tag,
  coupon: Ticket,
  event: Calendar,
  job_posting: Briefcase,
  portfolio: Image,
  video: Video,
  product: ShoppingBag,
  real_estate: Home,
};

export default function ContentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadContent();
  }, [id]);
  
  const loadContent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/directory/content/${id}`);
      if (!res.ok) throw new Error('Content not found');
      
      const data = await res.json();
      setContent(data);
      
      // Load business info if available
      if (data.business_id) {
        const bizRes = await fetch(`${API_URL}/api/directory/businesses/${data.business_id}`);
        if (bizRes.ok) {
          setBusiness(await bizRes.json());
        }
      }
    } catch (error) {
      console.error('Failed to load content:', error);
      toast.error('Content not found');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }
  
  if (!content) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Content Not Found</h1>
          <Link to="/directory">
            <Button variant="outline" className="border-cyan-500/30 text-cyan-400">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Directory
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const ContentIcon = CONTENT_ICONS[content.content_type] || FileText;
  
  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#0a0f1a]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <Link to="/directory" className="text-cyan-400 hover:text-cyan-300 text-sm">
            View Directory
          </Link>
        </div>
      </nav>
      
      {/* Hero Image/Video */}
      {(content.images?.[0] || content.image_url || content.video_url) && (
        <div className="relative">
          {content.video_url ? (
            <div className="w-full aspect-video bg-black">
              {content.video_url.includes('youtube') || content.video_url.includes('youtu.be') ? (
                <iframe
                  src={content.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  className="w-full h-full"
                  allowFullScreen
                  title={content.title}
                />
              ) : content.video_url.includes('vimeo') ? (
                <iframe
                  src={content.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                  className="w-full h-full"
                  allowFullScreen
                  title={content.title}
                />
              ) : (
                <video 
                  src={content.video_url} 
                  controls 
                  className="w-full h-full object-contain"
                />
              )}
            </div>
          ) : (
            <img 
              src={content.images?.[0] || content.image_url}
              alt={content.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          )}
        </div>
      )}
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Type Badge & Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-500/20 text-purple-400 capitalize flex items-center gap-2">
              <ContentIcon className="w-4 h-4" />
              {content.content_type.replace(/_/g, ' ')}
            </span>
            {content.is_featured && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-500/20 text-amber-400">
                Featured
              </span>
            )}
            <span className="text-gray-500 text-sm flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(content.created_at)}
            </span>
          </div>
          
          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {content.title}
          </h1>
          
          {/* Business Info */}
          {business && (
            <Link 
              to={`/directory/business/${business.id}`}
              className="inline-flex items-center gap-3 p-3 bg-white/5 rounded-xl mb-6 hover:bg-white/10 transition-colors"
            >
              {business.logo ? (
                <img src={business.logo} alt={business.name} className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                  <Building className="w-5 h-5 text-cyan-400" />
                </div>
              )}
              <div>
                <p className="font-medium text-white">{business.name}</p>
                <p className="text-xs text-gray-500">{business.city}, {business.state}</p>
              </div>
            </Link>
          )}
          
          {/* Description */}
          <div className="prose prose-invert prose-cyan max-w-none mb-8">
            <p className="text-lg text-gray-300 leading-relaxed">
              {content.description}
            </p>
          </div>
          
          {/* Full Content */}
          {content.content && (
            <div className="bg-white/5 rounded-2xl p-6 mb-8">
              <div 
                className="prose prose-invert prose-cyan max-w-none text-gray-300"
                dangerouslySetInnerHTML={{ __html: content.content }}
              />
            </div>
          )}
          
          {/* Additional Fields based on content type */}
          <div className="space-y-4 mb-8">
            {content.price && (
              <div className="flex items-center gap-2 text-lg">
                <span className="text-gray-500">Price:</span>
                <span className="text-green-400 font-bold">${content.price}</span>
              </div>
            )}
            {content.location && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Location:</span>
                <span className="text-white">{content.location}</span>
              </div>
            )}
            {content.event_date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-cyan-400" />
                <span className="text-white">{formatDate(content.event_date)}</span>
                {content.event_time && <span className="text-gray-400">at {content.event_time}</span>}
              </div>
            )}
            {content.external_url && (
              <a 
                href={content.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300"
              >
                <ExternalLink className="w-4 h-4" />
                Visit Link
              </a>
            )}
            {content.contact_email && (
              <a 
                href={`mailto:${content.contact_email}`}
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300"
              >
                Contact: {content.contact_email}
              </a>
            )}
          </div>
          
          {/* Tags */}
          {content.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {content.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-white/5 rounded-full text-sm text-gray-400">
                  #{tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Stats */}
          <div className="flex items-center gap-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 text-gray-400">
              <Eye className="w-4 h-4" />
              <span>{content.views || 0} views</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Heart className="w-4 h-4" />
              <span>{content.likes || 0} likes</span>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="mt-12 p-6 bg-gradient-to-br from-cyan-900/20 to-blue-900/20 rounded-2xl border border-cyan-500/20 text-center">
            <h3 className="text-xl font-bold text-white mb-2">Want to publish your own content?</h3>
            <p className="text-gray-400 mb-4">Join our directory and reach thousands of potential customers</p>
            <Link to="/register">
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold">
                Get Started
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
